/**
 * DeviceVisuals — shared "rich UI" building blocks for device / provider surfaces.
 *
 * §28.518.32 — these primitives replace the runtime __iabSirensActive shim
 * that used to decorate the live DOM. Everything is compiled JSX now (single
 * render, no MutationObserver, no post-mount DOM rewrites).
 *
 * Kept intentionally dependency-light so both Devices.tsx and AccessControl.tsx
 * (and any admin surface) can import without pulling the whole locks/BrandPicker
 * or the acs-detail modal tree.
 */
import React from 'react';
import './DeviceVisuals.css';

// ---------- vendor → local logo asset --------------------------------------

// Assets served from /mfg/{key}.svg (local, no external favicon service).
// Vendor names are customer-visible — customers know their own ACS/camera
// vendor. This is intentional: no obfuscation of customer-facing vendors,
// but zero external requests to third-party favicon services either.
export const VENDOR_ASSET_ID: Record<string, string> = {
  pdk: 'pdk',
  prodatakey: 'pdk',
  brivo: 'brivo',
  bridge: 'bridge',
  intrlock: 'bridge',
  'intrlock bridge': 'bridge',
  cbw: 'cbw',
  controlbyweb: 'cbw',
  turing: 'turing',
  turingvideo: 'turing',
  schlage: 'schlage',
  avigilon: 'avigilon',
  verkada: 'verkada',
  salto: 'salto',
  digitalwatchdog: 'digitalwatchdog',
};

const VENDOR_LABEL: Record<string, string> = {
  pdk: 'PDK',
  prodatakey: 'PDK',
  brivo: 'Brivo',
  turing: 'Turing',
  turingvideo: 'Turing',
  schlage: 'Schlage',
  bridge: 'Intrlock Bridge',
  intrlock: 'Intrlock',
  'intrlock bridge': 'Intrlock Bridge',
  avigilon: 'Avigilon',
  verkada: 'Verkada',
  salto: 'Salto',
  cbw: 'Control By Web',
  controlbyweb: 'Control By Web',
};

const VENDOR_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  pdk:       { bg: '#fff', fg: '#065f46', border: '#065f46' },
  brivo:     { bg: '#fff', fg: '#065f46', border: '#065f46' },
  turing:    { bg: '#fff', fg: '#1e40af', border: '#1e40af' },
  schlage:   { bg: '#fff', fg: '#7c2d12', border: '#7c2d12' },
  bridge:    { bg: '#fff', fg: '#a16207', border: '#a16207' },
  intrlock:  { bg: '#fff', fg: '#a16207', border: '#a16207' },
  avigilon:  { bg: '#fff', fg: '#374151', border: '#374151' },
  verkada:   { bg: '#fff', fg: '#4f46e5', border: '#4f46e5' },
  salto:     { bg: '#fff', fg: '#0f766e', border: '#0f766e' },
  cbw:       { bg: '#fff', fg: '#1e3a5f', border: '#1e3a5f' },
  controlbyweb: { bg: '#fff', fg: '#1e3a5f', border: '#1e3a5f' },
};

function providerKey(input: string): string {
  const raw = String(input || '').toLowerCase().trim();
  if (!raw) return '';
  if (raw.includes('bridge') || raw.includes('intrlock')) return 'bridge';
  if (raw.includes('pdk') || raw.includes('prodatakey')) return 'pdk';
  if (raw.includes('brivo')) return 'brivo';
  if (raw.includes('turing')) return 'turing';
  if (raw.includes('schlage')) return 'schlage';
  if (raw.includes('avigilon')) return 'avigilon';
  if (raw.includes('verkada')) return 'verkada';
  if (raw.includes('salto')) return 'salto';
  if (raw.includes('cbw') || raw.includes('controlbyweb') || raw.includes('control by web')) return 'cbw';
  return raw;
}

// ---------- ProviderPill ----------------------------------------------------

export interface ProviderPillProps {
  provider: string;                 // raw provider string ("pdk", "PDK", "Intrlock Bridge", etc.)
  label?: string;                   // override the shown label
  size?: 'sm' | 'md';
}

const _ProviderPill: React.FC<ProviderPillProps> = ({ provider, label, size = 'sm' }) => {
  const key = providerKey(provider);
  if (!key) return <span className="text-gray-400">—</span>;
  const assetId = VENDOR_ASSET_ID[key];
  const shown = label || VENDOR_LABEL[key] || key.toUpperCase();
  const colors = VENDOR_COLORS[key] || { bg: '#f3f4f6', fg: '#374151', border: '#9ca3af' };
  const px = size === 'md' ? '4px 12px' : '3px 10px';
  const fontSize = size === 'md' ? 12 : 11;
  const iconPx = size === 'md' ? 16 : 14;
  return (
    <span
      className="device-row-token"
      title={shown}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: px,
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        fontSize,
        fontWeight: 600,
        border: `1px solid ${colors.border}`,
      }}
    >
      {assetId && (
        <img
          src={`/mfg/${assetId}.${assetId === 'bridge' ? 'svg' : 'png'}`}
          alt=""
          loading="lazy"
          style={{ width: iconPx, height: iconPx, objectFit: 'contain' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
      )}
      <span>{shown}</span>
    </span>
  );
};
export const ProviderPill = React.memo(_ProviderPill);

// convenience helper for column render callbacks
export function providerPill(record: any): React.ReactNode {
  const p = record?.acs_provider || record?.provider || record?.manufacturer || '';
  return <ProviderPill provider={p} />;
}

// ---------- Intrlock canonical type ----------------------------------------
// §28.518.27 — one source of truth for how a device is presented across every
// surface. Reads canonical_device_class + canonical_type_detected first,
// falls back to legacy device_type / type / name heuristics.

export type IntrlockType =
  | 'Lock' | 'Siren' | 'Speaker' | 'Audio Bridge'
  | 'Camera' | 'Sensor' | 'Light' | 'Relay' | 'Device';

export function getIntrlockType(r: any): IntrlockType {
  const cls = String(r?.canonical_device_class || '').toLowerCase();
  const t   = String(r?.canonical_type_detected || r?.device_type || r?.type || '').toLowerCase();
  const nm  = String(r?.name || '').toLowerCase();
  if (cls === 'camera' || t === 'camera') return 'Camera';
  if (cls === 'siren' || /siren/.test(nm) || t === 'siren') return 'Siren';
  if (cls === 'audio_bridge' || t === 'audio_bridge' || nm.includes('audiobridge') || nm.includes('audio bridge')) return 'Audio Bridge';
  if (cls === 'speaker' || t === 'speaker' || /speaker/.test(nm)) return 'Speaker';
  if (cls === 'sensor' || /sensor|motion|glassbreak|smoke|dps/.test(t)) return 'Sensor';
  if (cls === 'light' || t === 'light' || t === 'lamp' || /light|lamp/.test(nm)) return 'Light';
  if (cls === 'acs_reader' || t === 'primaryreader' || t === 'auxiliaryreader' || t === 'reader' || t === 'lock' || t === 'door') return 'Lock';
  if (cls === 'output' || cls === 'relay' || t === 'output' || t === 'relay') return 'Relay';
  return 'Device';
}

export function intrlockTypeLabel(r: any): IntrlockType {
  return getIntrlockType(r);
}

export function mapIntrlockTypeToClass(t: IntrlockType): string {
  return ({
    Lock: 'acs_reader', Siren: 'siren', Speaker: 'speaker', 'Audio Bridge': 'audio_bridge',
    Camera: 'camera', Sensor: 'sensor', Light: 'light', Relay: 'output', Device: 'other',
  } as Record<IntrlockType, string>)[t] ?? 'other';
}

// ---------- DeviceTypeIcon --------------------------------------------------

type DeviceKind = 'lock' | 'siren' | 'audio_bridge' | 'speaker' | 'relay' | 'camera' | 'sensor' | 'light' | 'default';

function kindFromIntrlockType(t: IntrlockType): DeviceKind {
  switch (t) {
    case 'Lock':         return 'lock';
    case 'Siren':        return 'siren';
    case 'Audio Bridge': return 'audio_bridge';
    case 'Speaker':      return 'speaker';
    case 'Relay':        return 'relay';
    case 'Camera':       return 'camera';
    case 'Sensor':       return 'sensor';
    case 'Light':        return 'light';
    default:             return 'default';
  }
}

function classifyDevice(record: any): DeviceKind {
  return kindFromIntrlockType(getIntrlockType(record));
}

interface IconSpec {
  bg: string;
  border: string;
  svg: React.ReactNode;
  title: string;
}

const ICONS: Record<DeviceKind, IconSpec> = {
  lock: {
    bg: '#fef3c7', border: '#92400e', title: 'Lock',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    ),
  },
  siren: {
    bg: '#fee2e2', border: '#b91c1c', title: 'Siren',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
      </svg>
    ),
  },
  audio_bridge: {
    bg: '#d1fae5', border: '#047857', title: 'Audio Bridge',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
      </svg>
    ),
  },
  speaker: {
    bg: '#d1fae5', border: '#047857', title: 'Speaker',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
      </svg>
    ),
  },
  relay: {
    bg: '#fef9c3', border: '#a16207', title: 'Relay',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
      </svg>
    ),
  },
  camera: {
    bg: '#dbeafe', border: '#1d4ed8', title: 'Camera',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 7l-7 5 7 5V7z"></path>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
      </svg>
    ),
  },
  sensor: {
    bg: '#ffedd5', border: '#c2410c', title: 'Sensor',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
      </svg>
    ),
  },
  light: {
    bg: '#fef9c3', border: '#ca8a04', title: 'Light',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12c1 1 1.5 2 1.5 3h5c0-1 .5-2 1.5-3A7 7 0 0 0 12 2z"/>
      </svg>
    ),
  },
  default: {
    bg: '#f3f4f6', border: '#6b7280', title: 'Device',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
};

export interface DeviceTypeIconProps {
  record: any;
  offline?: boolean;
}

const _DeviceTypeIcon: React.FC<DeviceTypeIconProps> = ({ record, offline }) => {
  const kind = classifyDevice(record);
  const spec = ICONS[kind];
  const title = String(record?.device_type || record?.type || spec.title);
  const opacity = offline ? 0.4 : 1;
  return (
    <span
      className="device-row-token"
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: 999,
        background: spec.bg,
        color: spec.border,
        border: `1px solid ${spec.border}`,
        opacity,
      }}
    >
      {spec.svg}
    </span>
  );
};
export const DeviceTypeIcon = React.memo(_DeviceTypeIcon);

export function deviceTypeIcon(record: any, offline?: boolean): React.ReactNode {
  return <DeviceTypeIcon record={record} offline={offline} />;
}

export const DeviceStatusIcon: React.FC<{ online: boolean; label?: string }> = ({ online, label }) => (
  <span className={`device-row-token device-status-visual ${online ? 'online' : 'offline'}`} title={label || (online ? 'Online' : 'Offline')} aria-label={label || (online ? 'Online' : 'Offline')}>
    {online ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12.5l4 4L19 7"/></svg> : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M7 7l10 10M17 7L7 17"/></svg>}
  </span>
);

// ---------- Control button --------------------------------------------------

export interface ControlButtonProps {
  record: any;
  onClick?: (record: any) => void;
  disabled?: boolean;
  loading?: boolean;
}

export interface DeviceActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Compact is for table rows; touch keeps the same language with a 44px target. */
  surface?: 'compact' | 'touch';
  variant?: 'primary' | 'secondary' | 'danger';
  loading?: boolean;
  loadingLabel?: string;
  icon?: React.ReactNode;
}

const actionIconFor = (label: React.ReactNode): React.ReactNode => {
  const text = typeof label === 'string' ? label.toLowerCase() : '';
  if (text.includes('stream') || text.includes('feed')) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="5" width="13" height="14" rx="2"/><path d="m16 10 5-3v10l-5-3z"/></svg>;
  if (text.includes('siren')) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 15h12l-1-7a5 5 0 0 0-10 0l-1 7Z"/><path d="M4 19h16M12 2V0M4.5 5 2.8 3.5M19.5 5l1.7-1.5"/></svg>;
  if (text.includes('lock') && !text.includes('unlock')) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>;
  if (text.includes('access') || text.includes('unlock')) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M16 10V7a4 4 0 0 0-7.5-2"/></svg>;
  return null;
};

/** Canonical action for viewing or controlling a device on every web surface. */
export const DeviceActionButton = React.forwardRef<HTMLButtonElement, DeviceActionButtonProps>(({
  surface = 'compact', variant = 'primary', loading = false,
  loadingLabel = 'Working…', icon, className = '', disabled,
  children, type = 'button', ...props
}, ref) => {
  const resolvedIcon = icon ?? actionIconFor(children);
  return (
  <button
    ref={ref}
    type={type}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    className={`device-action-button device-action-button--${surface} device-action-button--${variant} ${surface === 'compact' ? 'device-row-token device-control-button' : ''} ${className}`}
    {...props}
  >
    {loading ? (
      <svg className="device-action-button__spinner" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" opacity=".25" />
        <path fill="currentColor" d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3z" />
      </svg>
    ) : resolvedIcon ? <span className="device-action-button__icon" aria-hidden="true">{resolvedIcon}</span> : null}
    <span>{loading ? loadingLabel : children}</span>
  </button>
  );
});
DeviceActionButton.displayName = 'DeviceActionButton';

export function controlLabelFor(record: any): string | null {
  // The Intrlock type is the presentation contract. Provider capabilities
  // authorize the backend operation, but must never relabel a Siren as a Lock
  // (or otherwise leak provider-specific semantics into the UI).
  const kind = classifyDevice(record);
  switch (kind) {
    case 'siren':        return 'Toggle Siren';
    case 'audio_bridge': return 'Control Audio Bridge';
    case 'speaker':      return 'Control Speaker';
    case 'lock':         return 'Grant Access';
    case 'relay':        return 'Grant Access';
    case 'light':        return 'Toggle Light';
    case 'camera':       return 'View Stream';
    case 'sensor':       return 'View State';
    default:             return 'View State';
  }
}

const _ControlButton: React.FC<ControlButtonProps> = ({ record, onClick, disabled, loading }) => {
  const label = controlLabelFor(record);
  if (!label) return null;
  return (
    <DeviceActionButton
      onClick={(e) => { e.stopPropagation(); if (!disabled && !loading && onClick) onClick(record); }}
      disabled={disabled || loading}
      loading={loading}
    >
      {label}
    </DeviceActionButton>
  );
};
export const ControlButton = React.memo(_ControlButton);

export function controlButtonFor(record: any, onClick?: (r: any) => void, opts?: { disabled?: boolean; loading?: boolean }): React.ReactNode {
  return <ControlButton record={record} onClick={onClick} disabled={opts?.disabled} loading={opts?.loading} />;
}

// ---------- §28.518.79 Wave U: Uniform shape helpers -------------------------
// SPA reads __intrlock first (7-day fallback grace).
// If __intrlock is absent (legacy adapter not yet migrated), falls back to the
// raw per-vendor fields that already exist in the record.

export interface UniformState {
  locked?: boolean;
  doorOpen?: boolean;
  triggered?: boolean;
  alarm?: boolean;
  playing?: boolean;
  lightOn?: boolean;
  streaming?: boolean;
  battery_pct?: number | null;
  firmware_version?: string;
  last_state_updated_at?: string;
}

/**
 * Extract canonical state from a device record.
 * Reads __intrlock.state if present; otherwise falls back to legacy PDK-style
 * raw fields so the SPA degrades gracefully during the 7-day grace period.
 */
export function getUniformState(record: any): UniformState {
  if (record?.__intrlock?.state) {
    return record.__intrlock.state as UniformState;
  }
  // Legacy fallback — emits console warning so we can track grace period exhaustion
  console.warn('[UNIFORM_FALLBACK_USED]', record?.acs_provider || record?.provider || record?.device_type || 'unknown', record?.id);
  return {
    locked: record?.relayOpen === false ? true : record?.relayOpen === true ? false : undefined,
    doorOpen: Boolean(record?.dpsOpen),
    triggered: Boolean(record?.rexOn || record?.propAlarmOn || record?.forcedAlarmOn || record?.alarmActive),
    alarm: Boolean(record?.propAlarmOn || record?.forcedAlarmOn || record?.alarmActive),
    battery_pct: record?.battery?.level ?? record?.battery_percent ?? null,
    firmware_version: record?.firmwareVersion ?? undefined,
    last_state_updated_at: record?.stateAsOf ?? record?.last_seen ?? undefined,
  };
}

/**
 * Returns true if the device is online per uniform shape.
 * Falls back to legacy communicationLost / connected fields.
 */
export function getUniformOnline(record: any): boolean | undefined {
  if (record?.__intrlock) {
    return record.__intrlock.online as boolean;
  }
  console.warn('[UNIFORM_FALLBACK_USED]', 'online', record?.id);
  if (typeof record?.communicationLost === 'boolean') return !record.communicationLost;
  if (typeof record?.connected === 'boolean') return record.connected;
  return undefined;
}

/**
 * Returns capabilities array from uniform shape, or a best-guess from legacy fields.
 * Used to gate action buttons without provider sniffing.
 */
export function getUniformCapabilities(record: any): string[] {
  if (record?.__intrlock?.capabilities) {
    return record.__intrlock.capabilities as string[];
  }
  // Legacy fallback heuristic
  const kind = kindFromIntrlockType(getIntrlockType(record));
  switch (kind) {
    case 'lock':   return ['unlock', 'pulse'];
    case 'relay':  return ['unlock', 'pulse', 'toggle'];
    case 'camera': return ['view_feed'];
    case 'light':  return ['toggle_light'];
    case 'siren':  return ['toggle_siren'];
    default:       return [];
  }
}
